import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getInsights, getUnreadCounts, getGroups } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useNewSessionModal } from '../context/NewSessionModalContext';
import LoadingLeaf from '../components/LoadingLeaf';

export default function Dashboard() {
  const [insights, setInsights] = useState(null);
  const [groups, setGroups] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { openNewSession } = useNewSessionModal();

  useEffect(() => {
    if (user) {
      fetchInsights();
      fetchUnreadCounts();
      fetchGroups();
    }
  }, [user]);

  // Kept independent (not Promise.all-ed) — insights is the primary content this
  // page exists to show, and unread counts / groups are secondary widgets. Bundling
  // them meant a hiccup in either secondary call (e.g. a slow cold-start response)
  // blanked the whole dashboard, including the clients list, since Promise.all
  // rejects the whole batch if any one call fails. Same fix as Clients.js's
  // fetchClients/fetchUnreadCounts split.
  const fetchInsights = async () => {
    try {
      const insightsRes = await getInsights();
      setInsights(insightsRes.data);
    } catch (err) {
      setError('Could not load dashboard.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCounts = async () => {
    try {
      const unreadRes = await getUnreadCounts();
      setUnreadCounts(unreadRes.data || {});
    } catch (err) {
      // non-critical — unread badges just won't show this load
    }
  };

  const fetchGroups = async () => {
    try {
      const groupsRes = await getGroups();
      setGroups(groupsRes.data || []);
    } catch (err) {
      // non-critical — groups card just won't show this load
    }
  };

  const greeting = () => {
    const h = new Date().getHours();
    const name = profile?.full_name?.split(' ')[0] || user?.user_metadata?.full_name?.split(' ')[0] || 'Coach';
    if (h < 12) return `Good morning, ${name}`;
    if (h < 17) return `Good afternoon, ${name}`;
    return `Good evening, ${name}`;
  };

  const today = new Date().toLocaleDateString('default', {
    weekday: 'long', month: 'long', day: 'numeric'
  });

  if (loading) return <LoadingLeaf fullScreen size={72} />;

  const clients = insights?.clients || [];
  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);
  const hasClients = clients.length > 0;
  const hasGroups = groups.length > 0;

  return (
    <div style={styles.page}>

      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={styles.greeting}>{greeting()}</div>
          <div style={styles.date}>{today}</div>
        </div>
      </div>

      {error && <div style={styles.errorAlert}>{error}</div>}

      {/* Primary actions */}
      <div style={styles.actionsGrid}>
        <button style={styles.actionCard} className="tc-card-hover" onClick={() => navigate('/clients?new=1')}>
          <div style={styles.actionIcon}>
            <svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="6" cy="5" r="2.5" /><path d="M1 13c0-2.8 2.2-5 5-5s5 2.2 5 5" /><circle cx="12" cy="5" r="2" /><path d="M12 9c1.7.3 3 1.8 3 3.5" /></svg>
          </div>
          <div>
            <div style={styles.actionLabel}>New client</div>
            <div style={styles.actionSub}>Add someone to your workspace</div>
          </div>
        </button>
        {profile?.session_focus !== 'individual' && (
          <button style={styles.actionCard} className="tc-card-hover" onClick={() => navigate('/groups?new=1')}>
            <div style={styles.actionIcon}>◈</div>
            <div>
              <div style={styles.actionLabel}>New group</div>
              <div style={styles.actionSub}>Tag clients, build a shared modality</div>
            </div>
          </button>
        )}
        <button style={styles.actionCard} className="tc-card-hover" onClick={() => openNewSession()}>
          <div style={styles.actionIcon}>◎</div>
          <div>
            <div style={styles.actionLabel}>Quick start session</div>
            <div style={styles.actionSub}>Record and summarize a session</div>
          </div>
        </button>
      </div>

      {/* Clients / Groups / Unread — only once there's something to show */}
      {(hasClients || hasGroups || totalUnread > 0) && (
        <div style={styles.grid}>
          {hasClients && (
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <span style={styles.cardTitle}>Clients</span>
                <button style={styles.cardAction} onClick={() => navigate('/clients')}>View all →</button>
              </div>
              <div style={styles.cardBody}>
                {clients.slice(0, 6).map((client, i) => (
                  <div
                    key={client.id}
                    style={{ ...styles.row, ...(i === Math.min(clients.length, 6) - 1 ? { borderBottom: 'none' } : {}) }}
                    className="tc-card-hover"
                    onClick={() => navigate(`/clients/${client.id}`)}
                  >
                    <div style={styles.initials}>
                      {client.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <div style={styles.rowInfo}>
                      <div style={styles.rowTitle}>
                        {client.name}
                        {unreadCounts[client.id] > 0 && <span style={styles.unreadDot}>{unreadCounts[client.id]}</span>}
                      </div>
                      <div style={styles.rowSub}>{client.session_type}</div>
                    </div>
                    <span style={styles.chevron}>→</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasGroups && (
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <span style={styles.cardTitle}>Groups</span>
                <button style={styles.cardAction} onClick={() => navigate('/groups')}>View all →</button>
              </div>
              <div style={styles.cardBody}>
                {groups.slice(0, 6).map((group, i) => (
                  <div
                    key={group.id}
                    style={{ ...styles.row, ...(i === Math.min(groups.length, 6) - 1 ? { borderBottom: 'none' } : {}) }}
                    className="tc-card-hover"
                    onClick={() => navigate(`/groups/${group.id}`)}
                  >
                    <div style={styles.initials}>◎</div>
                    <div style={styles.rowInfo}>
                      <div style={styles.rowTitle}>{group.name}</div>
                      <div style={styles.rowSub}>{group.client_count || 0} client{group.client_count !== 1 ? 's' : ''}</div>
                    </div>
                    <span style={styles.chevron}>→</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {totalUnread > 0 && (
            <div style={{ ...styles.card, borderColor: 'rgba(194,94,94,0.3)' }}>
              <div style={styles.cardHeader}>
                <span style={styles.cardTitle}>💬 Unread messages</span>
                <button style={styles.cardAction} onClick={() => navigate('/clients')}>View all</button>
              </div>
              <div style={styles.cardBody}>
                {Object.entries(unreadCounts).map(([clientId, count], i) => {
                  const client = clients.find(c => c.id === clientId);
                  if (!client) return null;
                  return (
                    <div
                      key={clientId}
                      style={{ ...styles.row, ...(i === Object.keys(unreadCounts).length - 1 ? { borderBottom: 'none' } : {}) }}
                      className="tc-card-hover"
                      onClick={() => navigate(`/clients/${clientId}`)}
                    >
                      <div style={styles.initials}>
                        {client.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div style={styles.rowInfo}>
                        <div style={styles.rowTitle}>{client.name}</div>
                        <div style={styles.rowSub}>{client.session_type}</div>
                      </div>
                      <span style={styles.unreadPill}>{count} unread</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { padding: '36px 40px', fontFamily: "'DM Sans', sans-serif" },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '28px' },
  greeting: { fontFamily: "'Lora', Georgia, serif", fontSize: '28px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '4px' },
  date: { fontSize: '13px', color: 'var(--text-soft)' },
  errorAlert: { background: 'var(--red-light)', color: 'var(--red)', padding: '12px 16px', borderRadius: '8px', fontSize: '13.5px', marginBottom: '16px' },
  actionsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '32px' },
  actionCard: { display: 'flex', alignItems: 'center', gap: '16px', background: 'white', borderRadius: '14px', border: '1px solid var(--border)', padding: '22px', cursor: 'pointer', boxShadow: 'var(--shadow-sm)', textAlign: 'left', fontFamily: "'DM Sans', sans-serif" },
  actionIcon: { width: '44px', height: '44px', borderRadius: '10px', background: 'var(--mint)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: 'var(--sage)', flexShrink: 0 },
  actionLabel: { fontFamily: "'Lora', Georgia, serif", fontSize: '16px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '2px' },
  actionSub: { fontSize: '12px', color: 'var(--text-soft)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', alignItems: 'start' },
  card: { background: 'white', borderRadius: '14px', border: '1px solid var(--border)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' },
  cardHeader: { padding: '16px 22px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  cardTitle: { fontSize: '13.5px', fontWeight: '600', color: 'var(--text-dark)', letterSpacing: '0.01em' },
  cardAction: { fontSize: '12.5px', color: 'var(--sage)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: '500' },
  cardBody: { padding: '6px 0' },
  row: { display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 22px', borderBottom: '1px solid var(--border-light)', cursor: 'pointer' },
  initials: { width: '32px', height: '32px', borderRadius: '50%', background: 'var(--mint)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '600', color: 'var(--sage)', flexShrink: 0 },
  rowInfo: { flex: 1, minWidth: 0 },
  rowTitle: { fontSize: '13.5px', fontWeight: '500', color: 'var(--text-dark)', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '8px' },
  rowSub: { fontSize: '12px', color: 'var(--text-soft)' },
  unreadDot: { background: 'var(--red-light)', color: 'var(--red)', border: '1px solid rgba(194,94,94,0.25)', borderRadius: '20px', fontSize: '10px', fontWeight: '600', padding: '1px 7px' },
  unreadPill: { background: 'var(--red-light)', color: 'var(--red)', border: '1px solid rgba(194,94,94,0.25)', borderRadius: '20px', fontSize: '11px', fontWeight: '600', padding: '3px 10px', flexShrink: 0 },
  chevron: { color: 'var(--text-faint)', fontSize: '14px' },
};
